if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();

        this.selectors = {
          viewer: '[id^="GalleryViewer"]',
          thumbnails: '[id^="GalleryThumbnails"]',
          mediaList: '[id^="Slider-Gallery"]',
          mediaItems: ['.product__media-item'],
        };

        this.initialized = false;
        this.sliderInstance = false;
        this.thumbsInstance = false;
        this.deferredLoaded = false;
      }

      connectedCallback() {
        DBAgency.Motion.inView(this, this.init.bind(this));
      }

      init() {
        if (this.initialized === true) return;

        this.elements = window.DBAgency.utils.queryDomNodes(this.selectors, this);
        this.mediaLayout = this.dataset.mediaLayout;
        this.onlyImage = this.dataset.onlyImage === 'true';
        this.enableDesktopSlider = this.dataset.enableDesktopSlider === 'true';
        this.enableMobileThumbnails = this.dataset.enableMobileThumbnails === 'true';
        this.enableImageZoom = this.dataset.enableImageZoom === 'true';
        this.totalMediaCount = parseInt(this.dataset.totalMediaCount) || 0;
        this.initialMediaLimit = parseInt(this.dataset.initialMediaLimit) || this.totalMediaCount;
        this.gridThumbnails = this.dataset.gridThumbnails === 'true';
        this.setSliderOptions();

        this.mql = window.matchMedia(DBAgency.config.mediaQueryMobile);
        this.mql.onchange = this.updateMediaLayout.bind(this);
        this.updateMediaLayout();

        this.bindGridThumbnails();

        if (this.enableImageZoom) {
          const allMedia = [...this.querySelectorAll('.product__media-item:not(.swiper-slide-duplicate)')];
          this.initImageZoom(allMedia);
        }

        this.initialized = true;
        this.scheduleDeferredLoad();
      }

      // Appele par product-info.js juste avant de remplacer la galerie (photos par couleur).
      // Pas dans disconnectedCallback : une galerie simplement deplacee dans la page
      // repasserait par connectedCallback sans se reinitialiser, et resterait cassee.
      destroy() {
        if (this.mql) this.mql.onchange = null;
        this.teardownGridThumbsObserver();
        if (typeof this.sliderInstance === 'object') {
          this.sliderInstance.slider.destroy();
          this.sliderInstance = false;
        }
        if (typeof this.thumbsInstance === 'object') {
          this.thumbsInstance.slider.destroy();
          this.thumbsInstance = false;
        }
        this.destroyLightbox();
      }

      setSliderOptions() {
        const mediaItemGap = parseInt(this.dataset.mediaItemGap);
        // L'ecart des vignettes et le nombre visible sur mobile sont regles a part.
        // Ces valeurs etaient ecrites en double, ici et dans le CSS : elles viennent
        // desormais d'une seule source, le reglage de la section.
        const thumbsGap = parseInt(this.dataset.thumbnailsGap) || mediaItemGap;
        const slidesView = parseInt(this.dataset.slidesView) || 5;

        this.sliderOptions = {
          init: false,
          slidesPerView: '1',
          spaceBetween: mediaItemGap,
          loop: false,
          grabCursor: true,
          allowTouchMove: true,
          autoHeight: true,
          navigation: {
            nextEl: this.querySelector('.swiper-button-next'),
            prevEl: this.querySelector('.swiper-button-prev'),
          },
          pagination: {
            el: this.querySelector('.swiper-pagination'),
            clickable: true,
            type: 'fraction',
          },
          threshold: 2,
        };

        this.thumbsOptions = {
          slidesPerView: Math.max(3, slidesView - 1),
          breakpoints: {
            461: {
              slidesPerView: slidesView,
            },
          },
          spaceBetween: thumbsGap,
          loop: false,
          freeMode: true,
          watchSlidesProgress: true,
          threshold: 2,
        };

        switch (this.mediaLayout) {
          case 'vertical-carousel':
            this.thumbsOptions = Object.assign({}, this.thumbsOptions, {
              breakpoints: {
                768: {
                  direction: 'vertical',
                  slidesPerView: 'auto',
                  spaceBetween: 0,
                },
              },
            });
            break;
          case 'slider-freemode':
            this.sliderOptions = Object.assign({}, this.sliderOptions, {
              slidesPerView: 'auto',
            });
            break;
        }
      }

      updateMediaLayout() {
        if (DBAgency.config.mqlMobile) {
          this.teardownGridThumbsObserver();
          this.initSlider();
        } else {
          if (this.enableDesktopSlider) {
            this.teardownGridThumbsObserver();
            this.initSlider();
          } else {
            this.destroySlider();
            this.initGridThumbsObserver();
          }
        }
      }

      // --- Vignettes posees sur l'image, en disposition grille ---------------------
      // Pas de Swiper ici : sans slider principal, le module Thumbs n'a rien a piloter,
      // et un Swiper vertical entrerait en conflit avec le defilement natif de la colonne.
      // La colonne reste du DOM simple, on lui ajoute un clic et un etat actif.

      bindGridThumbnails() {
        if (!this.gridThumbnails || !this.elements.thumbnails) return;

        DBAgency.utils.addEventDelegate({
          selector: '.product__thumbs-item',
          context: this.elements.thumbnails,
          handler: (event, target) => {
            // Sur mobile et en carrousel, le Swiper gere deja le clic.
            if (DBAgency.config.mqlMobile) return;
            if (typeof this.sliderInstance === 'object') return;

            this.scrollToMedia(target.dataset.target);
          },
        });
      }

      // Le decalage se LIT depuis le CSS deja resolu (`top` de la colonne collante),
      // menu epingle compris : aucune formule dupliquee entre les deux couches.
      // Storm, lui, ne decale pas du tout et laisse le menu recouvrir l'image.
      gridThumbnailsOffset() {
        if (!this.elements.thumbnails) return 0;
        const top = parseFloat(getComputedStyle(this.elements.thumbnails).top);
        return Number.isNaN(top) ? 0 : top;
      }

      scrollToMedia(mediaId) {
        const media = this.querySelector(
          `.product__media-item:not(.swiper-slide-duplicate)[data-media-id="${mediaId}"]`
        );
        if (!media) return;

        const top = media.getBoundingClientRect().top + window.pageYOffset - this.gridThumbnailsOffset();
        window.scrollTo({
          top: top,
          behavior: DBAgency.config.disableAnimations ? 'auto' : 'smooth',
        });
        this.setGridThumbActive(mediaId);
      }

      initGridThumbsObserver() {
        if (!this.gridThumbnails || !this.elements.thumbnails) return;
        this.teardownGridThumbsObserver();

        // En grille, deux images partagent la meme rangee : au centre de l'ecran, l'une
        // et l'autre sont visibles en meme temps. On tient donc la liste des images
        // visibles et on retient toujours la PREMIERE dans l'ordre de la page -- sinon
        // un clic sur l'image 5 surlignerait la 6, sa voisine de rangee.
        this.gridThumbsVisible = new Set();

        this.gridThumbsObserver = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting) this.gridThumbsVisible.add(entry.target);
              else this.gridThumbsVisible.delete(entry.target);
            });

            if (!this.gridThumbsVisible.size) return;

            let first = null;
            this.gridThumbsVisible.forEach((el) => {
              if (!first || el.compareDocumentPosition(first) & Node.DOCUMENT_POSITION_FOLLOWING) first = el;
            });
            if (first) this.setGridThumbActive(first.dataset.mediaId);
          },
          { rootMargin: '-45% 0px -45% 0px', threshold: 0 }
        );

        this.querySelectorAll('.product__media-item:not(.swiper-slide-duplicate)').forEach((item) => {
          this.gridThumbsObserver.observe(item);
        });
      }

      teardownGridThumbsObserver() {
        if (this.gridThumbsObserver) {
          this.gridThumbsObserver.disconnect();
          this.gridThumbsObserver = false;
        }
        if (this.gridThumbsVisible) this.gridThumbsVisible.clear();
      }

      setGridThumbActive(mediaId) {
        if (!this.elements.thumbnails) return;

        const thumbs = this.elements.thumbnails.querySelectorAll('.product__thumbs-item');
        let active = null;
        thumbs.forEach((thumb) => {
          const isActive = thumb.dataset.target === String(mediaId);
          thumb.classList.toggle('swiper-slide-thumb-active', isActive);
          if (isActive) active = thumb;
        });

        // Garder la vignette active visible SANS toucher au defilement de la page :
        // `scrollIntoView` remonterait les ancetres scrollables et detournerait la page.
        if (active && this.elements.thumbnails.scrollHeight > this.elements.thumbnails.clientHeight) {
          this.elements.thumbnails.scrollTop =
            active.offsetTop - (this.elements.thumbnails.clientHeight - active.offsetHeight) / 2;
        }
      }

      initSlider() {
        if (typeof this.sliderInstance !== 'object') {
          if ((this.enableDesktopSlider || this.enableMobileThumbnails) && this.elements.thumbnails) {
            this.thumbsInstance = new window.DBAgency.Carousel(this.elements.thumbnails, this.thumbsOptions);
            this.thumbsInstance.init();

            this.sliderOptions.thumbs = {
              swiper: this.thumbsInstance.slider,
              autoScrollOffset: 2,
            };
          }

          this.sliderInstance = new window.DBAgency.Carousel(this.elements.viewer, this.sliderOptions, [
            DBAgency.Swiper.Thumbs,
          ]);
          this.sliderInstance.init();

          this.handleSliderAfterInit();
          this.handleSlideChange();

          this.sliderInstance.slider.init();
          this.syncThumbsPosition();
        }
      }

      destroySlider() {
        if (typeof this.sliderInstance === 'object') {
          this.sliderInstance.slider.destroy();
          this.sliderInstance = false;
        }

        // En disposition grille la colonne de vignettes est du DOM simple : un Swiper
        // survivant y laisserait les styles inline d'un carrousel horizontal.
        if (this.gridThumbnails && typeof this.thumbsInstance === 'object') {
          this.thumbsInstance.slider.destroy(true, true);
          this.thumbsInstance = false;
          // initSlider ne repeuple `thumbs` que dans sa branche de creation : un objet
          // Swiper detruit qui y trainerait ferait echouer le retour vers mobile.
          delete this.sliderOptions.thumbs;
        }
      }

      scheduleDeferredLoad() {
        if (this.deferredLoaded || !this.querySelector('[id^="DeferredSlides"]')) {
          this.deferredLoaded = true;
          return;
        }

        if ('requestIdleCallback' in window) {
          requestIdleCallback(() => this.loadDeferredSlides(), { timeout: 1500 });
        } else {
          setTimeout(() => this.loadDeferredSlides(), 200);
        }
      }

      ensureDeferredLoaded() {
        if (!this.deferredLoaded) {
          this.loadDeferredSlides();
        }
      }

      loadDeferredSlides() {
        if (this.deferredLoaded) return;

        const deferredSlidesTemplate = this.querySelector('[id^="DeferredSlides"]');
        if (!deferredSlidesTemplate) {
          this.deferredLoaded = true;
          return;
        }

        const deferredSlides = [...deferredSlidesTemplate.content.querySelectorAll('.product__media-item')];
        const deferredThumbsTemplate = this.querySelector('[id^="DeferredThumbnails"]');

        if (typeof this.sliderInstance === 'object' && this.sliderInstance.slider) {
          const swiperWrapper = this.sliderInstance.slider.wrapperEl;
          deferredSlides.forEach((slide) => swiperWrapper.appendChild(slide.cloneNode(true)));

          if (typeof this.thumbsInstance === 'object' && this.thumbsInstance.slider && deferredThumbsTemplate) {
            const thumbsWrapper = this.thumbsInstance.slider.wrapperEl;
            const thumbs = [...deferredThumbsTemplate.content.querySelectorAll('.product__thumbs-item')];
            thumbs.forEach((thumb) => thumbsWrapper.appendChild(thumb.cloneNode(true)));
            this.thumbsInstance.slider.update();
          }

          this.sliderInstance.slider.update();
          this.syncThumbsPosition();
        } else {
          this.elements.mediaList.appendChild(deferredSlidesTemplate.content);

          // Hors slider, les vignettes differees n'etaient jamais injectees.
          if (deferredThumbsTemplate) {
            const thumbsList = this.elements.thumbnails
              ? this.elements.thumbnails.querySelector('.thumbnail-list')
              : null;
            if (thumbsList) thumbsList.appendChild(deferredThumbsTemplate.content);
          }
        }

        this.elements.mediaItems = [...this.querySelectorAll('.product__media-item:not(.swiper-slide-duplicate)')];

        if (this.enableImageZoom) {
          this.initImageZoom(this.elements.mediaItems);
        }

        this.initGridThumbsObserver();

        deferredSlidesTemplate.remove();
        deferredThumbsTemplate?.remove();
        this.deferredLoaded = true;
      }

      syncThumbsPosition() {
        if (typeof this.thumbsInstance !== 'object' || !this.thumbsInstance.slider) return;
        if (typeof this.sliderInstance !== 'object' || !this.sliderInstance.slider) return;

        const thumbsSlider = this.thumbsInstance.slider;
        const mainSlider = this.sliderInstance.slider;
        if (thumbsSlider.destroyed || mainSlider.destroyed) return;

        const realIndex = mainSlider.realIndex;

        // Manually update active class since slideToLoop uses runCallbacks: false,
        // which prevents the Thumbs module from updating the active state.
        thumbsSlider.slides?.forEach((slide, i) => {
          slide.classList.toggle('swiper-slide-thumb-active', i === realIndex);
        });

        thumbsSlider.slideTo(realIndex, 0);
      }

      destroyLightbox() {
        if (this.lightbox) {
          this.lightbox.destroy();
          this.lightbox = null;
        }
      }

      initThumbsSlider() {
        if (typeof this.thumbsInstance !== 'object') {
          this.thumbsInstance = new window.DBAgency.Carousel(this.selectors.thumbnails, this.thumbsOptions);
          this.thumbsInstance.init();
        }
      }

      // photoswipe.js (91 Ko) n'est plus charge d'office par la fiche produit : il retardait les
      // scripts du theme sur telephone (audit du 14/10/2026). main-product.liquid ne fait que
      // donner son adresse ; on le charge apres le chargement de la page, au repos, ou tout de
      // suite si l'on touche une photo avant. L'apercu rapide et « produit en vedette » le
      // chargent toujours eux-memes : pour eux, rien ne change.
      loadImageZoomLibrary() {
        if (window.DBAgency.PhotoSwipeLightbox) return Promise.resolve();
        // Apercu rapide hors fiche produit : ses scripts reinjectes arrivent dans le desordre, la
        // galerie peut demarrer avant photoswipe.js. On reprend alors l'adresse de sa balise.
        const existing = document.querySelector('script[src*="photoswipe.js"]');
        const src = window.DBAgency.photoswipeUrl || (existing && existing.src);
        if (!src) return Promise.reject(new Error('photoswipe.js : adresse inconnue'));
        if (!window.DBAgency.photoswipeLoading) {
          window.DBAgency.photoswipeLoading = new Promise((resolve, reject) => {
            const script = document.createElement('script');
            script.src = src;
            script.onload = resolve;
            script.onerror = () => {
              window.DBAgency.photoswipeLoading = null;
              reject(new Error('photoswipe.js : chargement impossible'));
            };
            document.head.appendChild(script);
          });
        }
        return window.DBAgency.photoswipeLoading;
      }

      deferImageZoom(items) {
        this.pendingZoomItems = items;
        if (this.imageZoomDeferred) return;
        this.imageZoomDeferred = true;

        const build = () => {
          if (!this.lightbox) this.initImageZoom(this.pendingZoomItems);
        };

        // Toucher une photo avant l'arrivee du script : on le charge, puis on ouvre.
        DBAgency.utils.addEventDelegate({
          selector: '.js-photoswipe--zoom',
          context: this,
          handler: (e, media) => {
            if (this.lightbox || media.dataset.mediaType !== 'image') return;
            const index = Number(media.dataset.mediaIndex) || 0;
            this.loadImageZoomLibrary()
              .then(() => {
                build();
                this.lightbox.loadAndOpen(index);
              })
              .catch((error) => console.error(error));
          },
        });

        const prefetch = () => {
          const run = () => this.loadImageZoomLibrary().then(build).catch((error) => console.error(error));
          if ('requestIdleCallback' in window) requestIdleCallback(run, { timeout: 2000 });
          else setTimeout(run, 1);
        };
        if (document.readyState === 'complete') prefetch();
        else window.addEventListener('load', prefetch, { once: true });
      }

      initImageZoom(items = []) {
        if (!window.DBAgency.PhotoSwipeLightbox) {
          this.deferImageZoom(items);
          return;
        }
        this.destroyLightbox();
        let dataSource = [];
        if (items.length > 0) {
          items.forEach((media) => {
            switch (media.dataset.mediaType) {
              case 'model':
                dataSource.push({
                  id: media.dataset.mediaIndex,
                  html: `<div class="pswp__item--${media.dataset.mediaType}">${
                    media.querySelector('product-model').outerHTML
                  }</div>`,
                  mediaType: media.dataset.mediaType,
                });
                break;
              case 'video':
              case 'external_video':
                dataSource.push({
                  id: media.dataset.mediaIndex,
                  html: `<div class="pswp__item--${media.dataset.mediaType}">${
                    media.querySelector('video-element').outerHTML
                  }</div>`,
                  mediaType: media.dataset.mediaType,
                });
                break;
              case 'image':
                dataSource.push({
                  id: media.dataset.mediaIndex,
                  src: media.dataset.src,
                  width: media.dataset.pswpWidth,
                  height: media.dataset.pswpHeight,
                  alt: media.dataset.alt || '',
                  mediaType: media.dataset.mediaType,
                });
                break;
            }
          });
        }

        this.lightbox = new window.DBAgency.PhotoSwipeLightbox({
          dataSource: dataSource,
          pswpModule: window.DBAgency.PhotoSwipe,
          bgOpacity: 1,
          arrowPrev: false,
          arrowNext: false,
          zoom: false,
          close: false,
          counter: false,
          preloader: false,
          preload: [1, 4],
        });

        this.lightbox.addFilter('thumbEl', (thumbEl, { id }, index) => {
          if (this.sliderInstance && this.sliderInstance.slider) {
            const { slides, activeIndex } = this.sliderInstance.slider;
            if (slides[activeIndex]) {
              const el = slides[activeIndex].querySelector('img');
              if (el) {
                return el;
              }
            }
          }

          return thumbEl;
        });

        this.lightbox.addFilter('placeholderSrc', (placeholderSrc, { data }) => {
          if (data.src) {
            return data.src;
          }
          return placeholderSrc;
        });

        this.lightbox.on('change', () => {
          window.pauseAllMedia(this);
        });

        this.lightbox.on('destroy', () => {
          const { currIndex } = this.lightbox.pswp;

          if (this.sliderInstance && this.sliderInstance.slider) {
            this.sliderInstance.slider.slideToLoop(currIndex, 0, false);
          }
        });

        this.lightbox.on('pointerDown', (e) => {
          if (this.lightbox.pswp.currSlide.data.mediaType != 'image') {
            e.preventDefault();
          }
        });

        this.lightbox.on('uiRegister', () => {
          if (!this.onlyImage) {
            this.lightbox.pswp.ui.registerElement({
              name: 'next',
              ariaLabel: DBAgency.accessibilityStrings.nextSlide,
              order: 3,
              isButton: true,
              html: '<svg class="pswp-icon-next flip-x" viewBox="0 0 100 100"><path d="M 10,50 L 60,100 L 65,90 L 25,50  L 65,10 L 60,0 Z"></path></svg>',
              onClick: (event, el) => {
                this.lightbox.pswp.next();
              },
            });
            this.lightbox.pswp.ui.registerElement({
              name: 'prev',
              ariaLabel: DBAgency.accessibilityStrings.previousSlide,
              order: 1,
              isButton: true,
              html: '<svg class="pswp-icon-prev rtl-flip-x" viewBox="0 0 100 100"><path d="M 10,50 L 60,100 L 65,90 L 25,50  L 65,10 L 60,0 Z"></path></svg>',
              onClick: (event, el) => {
                this.lightbox.pswp.prev();
              },
            });
          }
          this.lightbox.pswp.ui.registerElement({
            name: 'close-zoom',
            ariaLabel: DBAgency.accessibilityStrings.closeZoom,
            order: 2,
            isButton: true,
            html: '<svg xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false" role="presentation" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>',
            onClick: (event, el) => {
              this.lightbox.pswp.close();
            },
          });
        });

        this.lightbox.init();

        DBAgency.utils.addEventDelegate({
          selector: '.js-photoswipe--zoom',
          context: this,
          handler: (e, media) => {
            if (media.dataset.mediaType === 'image') {
              const index = Number(media.dataset.mediaIndex) || 0;
              this.lightbox.loadAndOpen(index);
            }
          },
        });
      }

      handleSliderAfterInit() {
        this.sliderInstance.slider.on('afterInit', (swiper) => {
          const { slides, activeIndex } = swiper;

          if (slides[activeIndex]) {
            const isModelMediaType = slides[activeIndex].dataset.mediaType === 'model';
            this.toggleSliderDraggableState(!isModelMediaType);
          }
        });
      }

      handleSlideChange() {
        this.sliderInstance.slider.on('realIndexChange', (swiper) => {
          const { slides, activeIndex, realIndex, thumbs } = swiper;

          if (thumbs.swiper) {
            thumbs.swiper.slideTo(realIndex);
          }

          if (slides[activeIndex]) {
            this.playActiveMedia(slides[activeIndex]);

            const isModelMediaType = slides[activeIndex].dataset.mediaType === 'model';
            this.toggleSliderDraggableState(!isModelMediaType);
          }
        });
      }

      toggleSliderDraggableState(isDraggable) {
        if (this.sliderInstance.slider.allowTouchMove !== isDraggable) {
          this.sliderInstance.slider.allowTouchMove = isDraggable;
        }
      }

      playActiveMedia(selected) {
        const deferredMedia = selected.querySelector('product-model');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      setActiveMedia(variant) {
        if (!variant || !variant.hasOwnProperty('featured_media') || !variant.featured_media) return;

        const targetPosition = variant.featured_media.position || 0;
        if (targetPosition > this.initialMediaLimit) {
          this.ensureDeferredLoaded();
        }

        if (typeof this.sliderInstance === 'object' && this.sliderInstance.slider) {
          // Le rang se lit dans la galerie AFFICHEE : filtree par couleur, elle ne suit
          // plus la position du media dans le produit (`featured_media.position`).
          const slides = [...this.querySelectorAll('.product__media-item:not(.swiper-slide-duplicate)')];
          const index = slides.findIndex((slide) => slide.dataset.mediaId == variant.featured_media.id);
          if (index < 0) return;

          this.sliderInstance.slider.slideToLoop(index, 0, false);
          this.syncThumbsPosition();
        } else if (this.gridThumbnails && !DBAgency.config.mqlMobile) {
          this.scrollToMedia(variant.featured_media.id);
        } else {
          this.sortMediaItems(variant);
        }
      }

      sortMediaItems(variant) {
        this.ensureDeferredLoaded();
        let newMedias = this.elements.mediaItems;

        // Reset ordering.
        newMedias.sort(function (a, b) {
          return a.dataset.mediaIndex - b.dataset.mediaIndex;
        });

        newMedias.some((media, index) => {
          if (media.dataset.mediaId == variant.featured_media.id) {
            const [element] = newMedias.splice(index, 1);
            newMedias.unshift(element);
            return true;
          }
        });

        this.elements.mediaList.innerHTML = '';
        newMedias.forEach((media) => {
          this.elements.mediaList.appendChild(media);
        });

        // Reset ordering.
        newMedias.sort(function (a, b) {
          return a.dataset.mediaIndex - b.dataset.mediaIndex;
        });

        if (this.enableImageZoom) {
          this.initImageZoom(newMedias);
        }

        if (!DBAgency.config.mqlMobile) {
          const selectedMedia = this.querySelector(`[data-media-id="${variant.featured_media.id}"]`);
          if (selectedMedia) {
            window.scrollTo({ top: selectedMedia.offsetTop, behavior: DBAgency.config.disableAnimations ? 'auto' : 'smooth' });
          }
        }
      }
    }
  );
}
